import { useCallback, useEffect, useMemo, useState } from 'react'
import { connectedBy, productOf } from '../lib/metaConnections.js'
import Icon from './icons.jsx'
import { useApp } from '../context/AppContext.jsx'
import {
  getMetaConnections, getMetaMessagingAvailable, getMetaMessagingChannels,
  linkMetaMessaging, unlinkMetaMessaging,
} from '../db/api.js'

const CLOSE_MS = 200

const LABEL = { fontSize: 12, fontWeight: 700, color: 'var(--sec)', marginBottom: 6, letterSpacing: '.05em' }
const ROW = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--soft)', fontSize: 14 }
const CHANNEL_LABEL = { instagram: 'Instagram', messenger: 'Messenger' }

// Um canal é identificado por tipo + id externo (o mesmo id pode existir em canais diferentes).
const keyOf = (c) => `${c.channel}:${c.external_id}`

// As mensagens vêm da conexão `messaging` (#344); anúncios, Facebook e Instagram orgânico ficam de fora.
const isMessagingConnection = (c) => c.is_active && productOf(c) === 'messaging'

/**
 * Modal "Instagram Direct e Messenger": vincula Páginas (Messenger) e contas do Instagram a um
 * cliente para que as mensagens cheguem e sejam respondidas no Atendimento.
 *
 * Os canais disponíveis vêm da conexão Mensagens da organização
 * (/api/meta-ads/messaging/available, sem token). O vínculo assina `messages` na Página; o token
 * de cada Página fica só no backend.
 *
 * Props:
 *  - open    : controla montagem.
 *  - onClose : chamado depois da animação de saída.
 */
export default function MetaMessagingModal({ open, onClose }) {
  const { availableClients, accessMode } = useApp()
  const clients = useMemo(() => availableClients || [], [availableClients])
  const canManage = accessMode !== 'client_only'

  const [mounted, setMounted] = useState(false)
  const [closing, setClosing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [connections, setConnections] = useState([])
  const [connectionId, setConnectionId] = useState('')
  const [available, setAvailable] = useState([])
  const [linked, setLinked] = useState([])
  const [selected, setSelected] = useState([])
  const [clientId, setClientId] = useState('')

  const clientName = (id) => clients.find((c) => c.id === id)?.name || 'Cliente'
  const linkedKeys = new Set(linked.filter((c) => c.is_active).map(keyOf))
  const pending = available.filter((c) => !linkedKeys.has(keyOf(c)))

  const startClose = useCallback(() => {
    setClosing(true)
    setTimeout(() => {
      setClosing(false)
      setMounted(false)
      onClose?.()
    }, CLOSE_MS)
  }, [onClose])

  const loadChannels = useCallback(async (connId, forClientId) => {
    setError('')
    try {
      const [options, current] = await Promise.all([
        connId ? getMetaMessagingAvailable(connId) : Promise.resolve([]),
        forClientId ? getMetaMessagingChannels(forClientId) : Promise.resolve([]),
      ])
      setAvailable(Array.isArray(options) ? options : [])
      setLinked(Array.isArray(current) ? current : [])
    } catch (e) {
      setError(e.message || 'Não foi possível carregar os canais.')
    }
  }, [])

  // A cada abertura: reseta o estado e recarrega a conexão e os canais.
  useEffect(() => {
    if (!open) return undefined
    let alive = true
    setMounted(true)
    setClosing(false)
    setError('')
    setSuccess('')
    setSelected([])
    setLoading(true)
    ;(async () => {
      try {
        const data = await getMetaConnections()
        const active = (Array.isArray(data) ? data : []).filter(isMessagingConnection)
        if (!alive) return
        setConnections(active)
        const first = active[0]?.id || ''
        setConnectionId(first)
        await loadChannels(first, clientId || clients[0]?.id || '')
      } catch (e) {
        if (alive) setError(e.message || 'Não foi possível carregar a conexão Meta.')
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
    // `clientId`/`clients` só definem o cliente inicial; recarregar a cada troca é do changeClient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loadChannels])

  useEffect(() => {
    if (!clientId && clients[0]?.id) setClientId(clients[0].id)
  }, [clients, clientId])

  // Esc fecha.
  useEffect(() => {
    if (!mounted) return undefined
    const onKey = (e) => e.key === 'Escape' && startClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mounted, startClose])

  const toggle = (key) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))

  async function changeConnection(id) {
    setConnectionId(id)
    setSelected([])
    await loadChannels(id, clientId)
  }

  async function changeClient(id) {
    setClientId(id)
    await loadChannels(connectionId, id)
  }

  async function handleLink() {
    if (!clientId || selected.length === 0) return
    setBusy(true)
    setError('')
    setSuccess('')
    try {
      const channels = selected.map((key) => {
        const [channel, ...rest] = key.split(':')
        return { channel, external_id: rest.join(':') }
      })
      const result = await linkMetaMessaging({ connectionId, clientId, channels })
      const failed = result.filter((c) => !c.subscribed)
      if (failed.length) {
        setError(`${failed.length} canal(is) vinculado(s), mas sem receber mensagens: ${failed[0].error}`)
      } else {
        setSuccess(`${result.length} canal(is) vinculado(s) a ${clientName(clientId)}.`)
      }
      setSelected([])
      await loadChannels(connectionId, clientId)
    } catch (e) {
      setError(e.message || 'Não foi possível vincular os canais.')
    } finally {
      setBusy(false)
    }
  }

  async function handleUnlink(channel) {
    if (!window.confirm(`Desligar o ${CHANNEL_LABEL[channel.channel]} "${channel.display_name || channel.external_id}"? As conversas continuam no Atendimento, mas novas mensagens deixam de chegar.`)) return
    setBusy(true)
    setError('')
    setSuccess('')
    try {
      await unlinkMetaMessaging(channel.id)
      setSuccess('Canal desligado.')
      await loadChannels(connectionId, clientId)
    } catch (e) {
      setError(e.message || 'Não foi possível desligar o canal.')
    } finally {
      setBusy(false)
    }
  }

  if (!mounted) return null

  return (
    <div
      className={`sup-overlay${closing ? ' is-closing' : ''}`}
      onMouseDown={(e) => e.target === e.currentTarget && startClose()}
    >
      <div className="sup-modal" role="dialog" aria-modal="true" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <button className="sup-close" onClick={startClose} aria-label="Fechar">
          <Icon n="x" s={16} />
        </button>

        <div className="sup-head">
          <div className="sup-title">Instagram Direct e Messenger</div>
          <div className="sup-subtitle">
            As mensagens chegam no Atendimento, na mesma caixa de entrada do WhatsApp. A resposta só sai
            nas 24 horas seguintes à última mensagem do contato.
          </div>
        </div>

        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && <div className="auth-error" role="alert">{error}</div>}
          {success && (
            <div
              className="auth-error"
              style={{ background: '#f0fdf4', color: '#16a34a', borderColor: '#bbf7d0' }}
              role="status"
            >
              {success}
            </div>
          )}

          {loading ? (
            <div className="empty"><div className="empty-desc">Carregando canais…</div></div>
          ) : connections.length === 0 ? (
            <div className="empty">
              <div className="empty-desc">
                Nenhuma conexão de Mensagens. Conecte as Mensagens em Conexões para escolher os canais.
              </div>
            </div>
          ) : (
            <>
              {connections.length > 1 && (
                <label style={{ fontSize: 13, color: 'var(--sec)' }}>
                  Conta Meta
                  <select
                    className="input"
                    style={{ display: 'block', width: '100%', marginTop: 4 }}
                    value={connectionId}
                    onChange={(e) => changeConnection(e.target.value)}
                    disabled={busy}
                  >
                    {connections.map((c) => (
                      <option key={c.id} value={c.id}>{connectedBy(c)}</option>
                    ))}
                  </select>
                </label>
              )}

              <label style={{ fontSize: 13, color: 'var(--sec)' }}>
                Cliente
                <select
                  className="input"
                  style={{ display: 'block', width: '100%', marginTop: 4 }}
                  value={clientId}
                  onChange={(e) => changeClient(e.target.value)}
                  disabled={busy}
                >
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </label>

              <section>
                <div style={LABEL}>VINCULADOS A {clientName(clientId).toUpperCase()}</div>
                {linked.filter((c) => c.is_active).length === 0 ? (
                  <p className="sup-subtitle">Nenhum canal vinculado a este cliente ainda.</p>
                ) : (
                  linked.filter((c) => c.is_active).map((c) => {
                    const ok = c.subscribed_at && !c.last_error
                    return (
                      <div key={c.id} style={ROW}>
                        <span>
                          <span style={{ fontWeight: 600 }}>{c.display_name || c.external_id}</span>
                          <span style={{ color: 'var(--sec)' }}> · {CHANNEL_LABEL[c.channel]}</span>
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ color: ok ? '#16a34a' : '#d97706', textAlign: 'right' }}>
                            {ok ? 'Recebendo mensagens' : 'Reautorizar'}
                          </span>
                          {canManage && (
                            <button className="btn btn-ghost btn-sm" onClick={() => handleUnlink(c)} disabled={busy}>
                              Desligar
                            </button>
                          )}
                        </span>
                      </div>
                    )
                  })
                )}
              </section>

              {canManage && (
                <section>
                  <div style={LABEL}>DISPONÍVEIS</div>
                  {pending.length === 0 ? (
                    <p className="sup-subtitle">
                      {available.length === 0
                        ? 'Nenhum canal encontrado. Reconecte as Mensagens e autorize o acesso às Páginas e ao Instagram.'
                        : 'Todos os canais desta conta já estão vinculados a este cliente.'}
                    </p>
                  ) : (
                    <>
                      {pending.map((c) => (
                        <label
                          key={keyOf(c)}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', fontSize: 14, cursor: 'pointer' }}
                        >
                          <input
                            type="checkbox"
                            checked={selected.includes(keyOf(c))}
                            onChange={() => toggle(keyOf(c))}
                            disabled={busy}
                          />
                          <span>
                            {c.name || c.external_id}
                            <span style={{ color: 'var(--sec)' }}> · {CHANNEL_LABEL[c.channel]}</span>
                          </span>
                        </label>
                      ))}
                      <button
                        className="btn btn-black"
                        style={{ marginTop: 12 }}
                        onClick={handleLink}
                        disabled={busy || !clientId || selected.length === 0}
                      >
                        {busy ? 'Vinculando…' : 'Vincular canais selecionados'}
                      </button>
                    </>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
